import { Card } from "@/components/ui/card";
import { useDailyHuddle } from "@/hooks/useDailyHuddle";
import { useTimezoneUtils } from "@/hooks/useTimezoneUtils";
import { useCompanyLocale } from "@/hooks/useCompanyLocale";
import { DollarSign, XCircle, TrendingUp, Phone, AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export const DailyHuddleRow = () => {
    const { data: metrics, isLoading } = useDailyHuddle();
    const { formatDate } = useTimezoneUtils();
    const { formatMoney: formatCurrency } = useCompanyLocale();

    if (isLoading) {
        return <Skeleton className="h-[96px] w-full rounded-lg" />;
    }

    const items = [
        {
            label: "Revenue closed",
            value: formatCurrency(metrics?.revenue || 0),
            icon: DollarSign,
            iconColor: "text-success",
        },
        {
            label: "Jobs cancelled",
            value: metrics?.jobsCancelled || 0,
            icon: XCircle,
            iconColor: "text-destructive",
        },
        {
            label: "Avg ticket",
            value: formatCurrency(metrics?.avgTicket || 0),
            icon: TrendingUp,
            iconColor: "text-info",
        },
        {
            label: "Booking rate",
            value: `${metrics?.bookingRate.toFixed(0)}%`,
            icon: Phone,
            iconColor: "text-primary",
        },
        {
            label: "Top cancellation reason",
            value: metrics?.topCancellationReason || "None",
            icon: AlertTriangle,
            iconColor: "text-warning",
            isText: true,
        },
    ];

    return (
        <Card className="overflow-hidden">
            <div className="flex flex-col divide-y divide-border lg:flex-row lg:items-stretch lg:divide-x lg:divide-y-0">
                {/* Label cell */}
                <div className="flex shrink-0 flex-col justify-center gap-1 bg-primary/10 px-5 py-4 lg:w-[168px]">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-primary">
                        Daily huddle
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-success" />
                        Live · {formatDate(new Date(), "HH:mm")}
                    </span>
                </div>

                {/* Metric cells */}
                {items.map((item) => {
                    const Icon = item.icon;
                    return (
                        <div
                            key={item.label}
                            className="flex min-w-0 flex-1 flex-col justify-center gap-1.5 px-5 py-4"
                        >
                            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                                <Icon className={cn("h-[13px] w-[13px] shrink-0", item.iconColor)} />
                                <span className="truncate">{item.label}</span>
                            </span>
                            {item.isText ? (
                                <span
                                    className="truncate text-[15px] font-semibold leading-6"
                                    title={String(item.value)}
                                >
                                    {item.value}
                                </span>
                            ) : (
                                <span className="text-2xl font-semibold tracking-tight tabular-nums">
                                    {item.value}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};
